/**
 * Per-project chat composer drafts.
 *
 * Previously the composer draft lived in a single global `cachedPrompt` cookie,
 * so text typed in one project's composer leaked into every other project's
 * input box (DEV-157). Drafts are now scoped per chat id and stored in
 * localStorage — the value is client-only (nothing server-side reads it), so a
 * cookie only wasted bandwidth on every request and risked the ~4KB cookie cap
 * once keyed per project.
 *
 * Drafts carry an `updatedAt` timestamp and are pruned after `TTL_MS`, so a
 * long-lived install doesn't accumulate a dead draft for every project ever
 * opened.
 */
import Cookies from 'js-cookie';

const PREFIX = 'chat-draft:';
// Bucket for the pre-project composer (home / new chat), which has no id yet.
const NEW_BUCKET = 'new';
// Legacy global key that caused the cross-project leak; cleaned up on load.
const LEGACY_COOKIE = 'cachedPrompt';
// Mirror the old 30-day cookie lifetime.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface DraftRecord {
  text: string;
  updatedAt: number;
}

function keyFor(id: string | undefined): string {
  return `${PREFIX}${id ?? NEW_BUCKET}`;
}

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

/** Read the saved draft for a chat id, or '' if none / expired / unparseable. */
export function getDraft(id: string | undefined): string {
  if (!hasLocalStorage()) {
    return '';
  }

  const key = keyFor(id);

  try {
    const raw = localStorage.getItem(key);

    if (!raw) {
      return '';
    }

    const record = JSON.parse(raw) as DraftRecord;

    if (!record || typeof record.text !== 'string' || typeof record.updatedAt !== 'number') {
      localStorage.removeItem(key);
      return '';
    }

    if (Date.now() - record.updatedAt > TTL_MS) {
      localStorage.removeItem(key);
      return '';
    }

    return record.text;
  } catch {
    // Corrupt entry — drop it so it can't wedge the composer.
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    return '';
  }
}

/** Persist (or clear, when blank) the draft for a chat id. */
export function setDraft(id: string | undefined, text: string): void {
  if (!hasLocalStorage()) {
    return;
  }

  const key = keyFor(id);
  const trimmed = text.trim();

  try {
    if (!trimmed) {
      localStorage.removeItem(key);
      return;
    }

    const record: DraftRecord = { text: trimmed, updatedAt: Date.now() };
    localStorage.setItem(key, JSON.stringify(record));
  } catch {
    // Quota or serialization failure — a lost draft is acceptable, a throw isn't.
  }
}

/** Remove the draft for a single chat id (called on submit / chat start). */
export function clearDraft(id: string | undefined): void {
  if (!hasLocalStorage()) {
    return;
  }

  try {
    localStorage.removeItem(keyFor(id));
  } catch {
    /* ignore */
  }
}

/**
 * Remove expired/corrupt drafts and the legacy global cookie. Call once on
 * load so stale drafts don't accumulate and existing users stop seeing the
 * leaked `cachedPrompt` text immediately.
 */
export function pruneDrafts(): void {
  // Kill the poison cookie that caused the cross-project leak.
  try {
    Cookies.remove(LEGACY_COOKIE);
  } catch {
    /* ignore */
  }

  if (!hasLocalStorage()) {
    return;
  }

  try {
    const now = Date.now();

    // Iterate backwards — removeItem shifts indices.
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);

      if (!key || !key.startsWith(PREFIX)) {
        continue;
      }

      try {
        const raw = localStorage.getItem(key);

        if (!raw) {
          localStorage.removeItem(key);
          continue;
        }

        const record = JSON.parse(raw) as DraftRecord;

        if (!record || typeof record.updatedAt !== 'number' || now - record.updatedAt > TTL_MS) {
          localStorage.removeItem(key);
        }
      } catch {
        localStorage.removeItem(key);
      }
    }
  } catch {
    /* ignore */
  }
}
